import BaseController from "confinus_ui/base_controller";
import FormFieldController from "confinus_ui/forms/form_field_controller";
import PurchaseConfiguratorExtraInfoController from "mercor/purchase_configurator/extra_info_controller";
import {
  calculateTotalPriceForDisplay,
  calculateTotalPriceNumber,
  isNullOrBigZero,
  parseVariantSize,
} from "lib/price_calculations";
import { getAppConfiguration } from "lib/app_configuration";
import { markAsSafeHTML, safelySetInnerHTML } from "lib/util/safe_html";
import {
  ConfinusUI__Forms__NumberFieldControllerIdentifier,
  ConfinusUI__Forms__SelectControllerIdentifier,
  Mercor__PurchaseConfigurator__ExtraInfoControllerIdentifier,
} from "controllers/identifiers";
import NumberFieldController from "confinus_ui/forms/number_field_controller";
import SelectController from "confinus_ui/forms/select_controller";

type ProductPriceChangedDetail = {
  totalPriceNumber: number;
  sku: string;
  variant: string;
  size: string | undefined;
  parsedSize: Big.Big | undefined;
  validSize: boolean;
  hasSize: boolean;
};

export type ProductPriceChangedEvent = CustomEvent<ProductPriceChangedDetail>;

interface VariantDataAttributes {
  sku: string;
  variantEncodedId: string;
  fallbackDisplay: string;
  extraChargeMultiplier: string | number;
  priceMultiplier: string | number;
  quantityType: "float" | "integer";
  sizeUnitConversionFactor?: string | number;
  sizeExcludedFromCalculation?: boolean;
  currency: string;
}

export default class OrderGuideRowController extends BaseController {
  public static targets = [
    "shortDescription",
    "sku",
    "variantSelector",
    "pricePerUom",
    "rowTotalPrice",
  ];

  public static values = {
    variantsQuantityData: Object,
  };

  declare private readonly variantsQuantityDataValue: {
    [key: string]: VariantDataAttributes;
  };

  declare private readonly shortDescriptionTarget: HTMLTableCellElement;
  declare private readonly skuTarget: HTMLTableCellElement;
  declare private readonly variantSelectorTarget: HTMLSelectElement;
  declare private readonly pricePerUomTargets: HTMLDivElement[];
  declare private readonly rowTotalPriceTarget: HTMLDivElement;

  public static outlets = [
    Mercor__PurchaseConfigurator__ExtraInfoControllerIdentifier,
    ConfinusUI__Forms__NumberFieldControllerIdentifier,
    ConfinusUI__Forms__SelectControllerIdentifier,
  ];
  declare private readonly mercorPurchaseConfiguratorExtraInfoOutlets: PurchaseConfiguratorExtraInfoController[];
  declare private readonly confinusUiFormsNumberFieldOutlets: NumberFieldController[];
  declare private readonly confinusUiFormsSelectOutlets: SelectController[];

  private get extraInfoControllers() {
    return this.mercorPurchaseConfiguratorExtraInfoOutlets;
  }

  declare protected quantityData: { [key: string]: VariantDataAttributes };

  onInitialize() {
    this.quantityData = this.variantsQuantityDataValue;

    return super.onInitialize();
  }

  connect() {
    super.connect();

    // Defer to next tick to ensure all child controllers have connected
    setTimeout(() => {
      this.displaySelectedVariant(this.variantSelectorTarget.value);
    }, 0);
  }

  private isCurrentConfigurationValid(hasRequiredSize: boolean) {
    const size = this.currentSizeField;
    const req = hasRequiredSize && this.currentQuantityField!.valid;
    return size ? size.valid && req : req;
  }

  public get selectedVariant() {
    return this.variantSelectorTarget.value;
  }

  public handleVariantChanged(evt: Event) {
    this.displaySelectedVariant(this.selectedVariant);
    this.updateCalculations();
  }

  public handlePortionSizeChanged(evt: Event) {
    const size = parseVariantSize(this.currentSize);
    if (size && size.gt(1000000)) {
      this.currentSizeField!.value = "1000000";
    }
    this.updateCalculations();
  }

  public handleQuantityChanged(evt: Event) {
    const qty = parseVariantSize(this.currentQuantity);
    if (qty && qty.gt(1000000)) {
      this.currentQuantityField!.value = "1000000";
    }
    this.updateCalculations();
  }

  private updateCalculations() {
    // Hard code something in case the data attr parsing goes belly-up
    let fallbackDisplay = "-";

    try {
      const variant = this.selectedVariant;
      const sku = this.quantityData[variant].sku;

      const quantity = this.currentQuantity;

      const size = this.currentSize;
      const parsedSize = parseVariantSize(size);
      const validSize = !isNullOrBigZero(parsedSize);
      const hasSize = size != undefined;

      const {
        currency,
        extraChargeMultiplier,
        priceMultiplier,
        quantityType,
        sizeExcludedFromCalculation,
        sizeUnitConversionFactor,
        ...other
      } = this.quantityData[variant];

      // We can now get the real fallback display to use
      fallbackDisplay = other.fallbackDisplay;

      // Generate and set the total price
      const opts = {
        currency,
        extraChargeMultiplier,
        locale: getAppConfiguration("locale"),
        priceMultiplier,
        quantity,
        quantityType,
        size,
        sizeExcludedFromCalculation,
        sizeUnitConversionFactor,
      };
      const totalPriceNumber = calculateTotalPriceNumber(opts);
      const totalPrice = calculateTotalPriceForDisplay(opts, totalPriceNumber);

      this.updateRowTotal(totalPrice !== null ? totalPrice : fallbackDisplay);
      //this.updateTotalPriceNumber(totalPriceNumber, size);

      // Update the info sections, if any
      this.extraInfoControllers.forEach((c) =>
        c.updateValue({ quantity, quantityType, size, fallbackDisplay }),
      );

      this.checkMarks(
        totalPriceNumber > 0,
        sku,
        variant,
        this.isCurrentConfigurationValid(hasSize ? validSize : true),
      );

      const detail: ProductPriceChangedDetail = {
        totalPriceNumber,
        sku,
        variant,
        size,
        parsedSize,
        validSize,
        hasSize,
      };

      // When a new estimated price is calculated on this row, we dispatch an event so a parent controller can update any totals.
      this.dispatch("product_price_change", {
        bubbles: true,
        cancelable: false,
        detail,
      });

      if (this.currentSizeField) {
        this.currentSizeField.required =
          hasSize && !validSize && totalPriceNumber > 0;
      }
    } catch (e) {
      // Ru-roh, something's gone wrong when parsing the attributes
      // TODO handle this error: show the user somehow
      console.error("Order guide calc error", e);
      this.updateRowTotal("!!");
    }
  }

  private updateRowTotal(formattedTotal: string) {
    safelySetInnerHTML(
      this.rowTotalPriceTarget,
      markAsSafeHTML(formattedTotal),
    );
  }

  private displaySelectedVariant(variant: string) {
    const toggler = (field: BaseController) => {
      const element = field.element as HTMLElement;

      // Check if the field element ID contains the variant encoded ID
      // The IDs are in format: "portion-size-{variant_id}" or "quantity-{variant_id}"
      let encodedId: string | undefined;

      if (element.id.startsWith("portion-size-")) {
        encodedId = element.id.replace("portion-size-", "");
      } else if (element.id.startsWith("quantity-")) {
        encodedId = element.id.replace("quantity-", "");
      } else {
        // Try to get from the Values API (for extra info controllers)
        encodedId = (field as any).variantEncodedIdValue;
      }

      if (encodedId != undefined) {
        const current = encodedId == variant;
        field.disabled = !current;
        this.toggleTargetElementClasses(element, current, ["hidden"], []);
      }
    };

    this.confinusUiFormsNumberFieldOutlets.forEach(toggler);
    this.confinusUiFormsSelectOutlets.forEach(toggler);
    this.extraInfoControllers.forEach(toggler);

    this.pricePerUomTargets.forEach((target) => {
      if (variant == target.getAttribute("data-variant-encoded-id")) {
        target.classList.remove("hidden");
      } else {
        target.classList.add("hidden");
      }
    });
  }

  private get currentQuantity(): string {
    return this.currentQuantityField!.value;
  }

  private get currentQuantityField() {
    const prefix = "quantity-";
    return this.confinusUiFormsNumberFieldOutlets.find((field) => {
      return (
        field.element.id.startsWith(prefix) &&
        field.element.id.slice(prefix.length) == this.selectedVariant
      );
    });
  }

  private get currentSize(): string | undefined {
    const field = this.currentSizeField;
    return field ? field.value : undefined;
  }

  private get currentSizeField() {
    const prefix = "portion-size-";
    let matcher = (field: any) =>
      field.element.id.startsWith(prefix) &&
      field.element.id.slice(prefix.length) == this.selectedVariant;
    let match = this.confinusUiFormsNumberFieldOutlets.find(matcher);
    if (match) {
      return match;
    } else {
      return this.confinusUiFormsSelectOutlets.find(matcher);
    }
  }

  public hideOnSearch(search: string) {
    const st = search.trim().toLowerCase();
    if (st.length < 2 || st == "") {
      this.element.classList.remove("hidden");
      return;
    }
    const matches =
      this.shortDescriptionTarget.innerText.toLowerCase().includes(st) ||
      this.sku.includes(st);

    this.element.classList.toggle("hidden", !matches);
  }

  public get sku() {
    return this.skuTarget.innerText.toLowerCase();
  }

  public sortableParam(name: string) {
    switch (name) {
      case "sku":
        return this.sku;
      case "description":
        return this.shortDescriptionTarget.innerText;
    }
  }

  private checkMarks(
    hasTotal: boolean,
    sku: string,
    variant: string,
    isValidConfiguration: boolean,
  ) {
    const group = document.getElementById(`order-guide-check-${sku}`);
    // set the visibility to hidden of all the child elements of group
    if (group) {
      for (const child of group.children) {
        (child as HTMLDivElement).style.display = "none";
      }
    }

    const el = document.getElementById(`order-guide-check-${sku}-${variant}`);
    const elWarn = document.getElementById(
      `order-guide-warn-${sku}-${variant}`,
    );
    if (isValidConfiguration) {
      if (el) el.style.display = hasTotal ? "block" : "none";
      if (elWarn) elWarn.style.display = "none";
    } else {
      if (el) el.style.display = "none";
      if (elWarn) elWarn.style.display = "block";
    }
  }
}
